import { useCallback, useEffect } from 'react';
import { useFormik } from 'formik';

import { FORWARD_DESCRIPTION_MAX_LENGTH, FORWARD_TITLE_MAX_LENGTH } from '@/forwarding/constants/forwardFormLimits';
import { createEmptyForm, ForwardForm } from '@/forwarding/types/ForwardForm';

const requiredValidationText = 'Required';
const titleTooLongValidationText = `Title is too long, max limit is ${FORWARD_TITLE_MAX_LENGTH} characters`;
const descriptionTooLongValidationText = `Description is too long, max limit is ${FORWARD_DESCRIPTION_MAX_LENGTH} characters`;

interface UseForwardModalFormOptions {
  isUrlFieldShown?: boolean;
  readOnly?: boolean;
}

export function useForwardModalForm(
  onSubmit: (formValues: ForwardForm) => void,
  initialForm: Nullable<ForwardForm> = null,
  options: UseForwardModalFormOptions = {},
) {
  const { isUrlFieldShown = false, readOnly = false } = options;

  const formik = useFormik<ForwardForm>({
    validateOnMount: false,
    validateOnChange: false,
    initialValues: initialForm ?? createEmptyForm(),
    validate: (values) => {
      if (readOnly) {
        return {};
      }

      const errors: Partial<Record<keyof ForwardForm, string>> = {};

      const title = values.title.trim();

      if (!isUrlFieldShown && title.length === 0) {
        errors.title = requiredValidationText;
      }

      if (title.length > FORWARD_TITLE_MAX_LENGTH) {
        errors.title = titleTooLongValidationText;
      }

      if (values.description.trim().length > FORWARD_DESCRIPTION_MAX_LENGTH) {
        errors.description = descriptionTooLongValidationText;
      }

      if (isUrlFieldShown && values.url.trim().length === 0) {
        errors.url = requiredValidationText;
      }

      return errors;
    },
    onSubmit: onSubmit,
  });

  useEffect(() => {
    formik.setValues(initialForm ?? createEmptyForm());
    formik.setErrors({});
  }, [initialForm]);

  const setFieldValue = useCallback(
    <T extends keyof ForwardForm>(name: T, val: ForwardForm[T]) => {
      formik.setFieldError(name, undefined);
      formik.setFieldValue(name, val);
    },
    [formik.setFieldError, formik.setFieldValue],
  );

  return { ...formik, setFieldValue };
}
